import React from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import Icon from '../../../../atoms/Icon'

// Shared row renderer for Wealthbox tasks. Used by:
//   - wealthboxMetric/WealthboxTaskPanel.jsx (metric tile's side panel)
//   - wealthboxList/WealthboxTasksContent.jsx (standalone list component)
// Keeping a single source of truth so a row tweak (status icon, ellipsis,
// overdue color, etc.) lands in both places. If a future caller needs a
// different row shape, fork at the call-site; don't add config props that
// only one caller uses.

export function getTaskStatus (task) {
  if (task?.complete) return 'completed'
  if (!task?.due_date) return 'open'
  const due = dayjs(task.due_date)
  if (!due.isValid()) return 'open'
  return due.isBefore(dayjs(), 'day') ? 'overdue' : 'open'
}

// Wealthbox `linked_to` is an unordered array with no `primary` flag - the
// first entry reflects insertion order in Wealthbox's UI. For row layout we
// only have horizontal space for one name plus an overflow hint, so we show
// the first entry and a `+N` count when there are more. Detail modals
// (WealthboxTaskDetailModal, WealthboxNoteDetailModal, WealthboxEventDetailModal)
// render the full list.
export function linkedContactName (task) {
  const links = Array.isArray(task?.linked_to) ? task.linked_to : []
  const first = links[0]?.name
  if (!first) return null
  return links.length > 1 ? `${first} (+${links.length - 1})` : first
}

function WealthboxTaskRow ({ task, onClick }) {
  const status = getTaskStatus(task)
  const dueFormatted = task.due_date ? dayjs(task.due_date).format('MMM D, YYYY') : null

  return (
    <div style={{ borderBottom: '1px solid var(--color-neutral-200)' }}>
      <div
        role='button'
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 1.5rem',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)'
        }}
        >
          <Icon
            name={status === 'completed' ? 'checkCircle' : status === 'overdue' ? 'clock' : 'radioUnchecked'}
            customSize='18px'
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className='font-size-xs' style={{
              color: 'var(--text-color-subtle)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {linkedContactName(task) || 'Unlinked'}
          </div>
          <div
            className='font-size-sm' style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {task.name || 'Untitled Task'}
          </div>
          {dueFormatted ? (
            <div
              className='font-size-xs' style={{
                color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)',
                marginTop: '2px'
              }}
            >
              {status === 'completed' ? 'Completed' : `Due ${dueFormatted}`}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

WealthboxTaskRow.propTypes = {
  task: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
}

export default WealthboxTaskRow
